import { useState } from "react";
import GeneralButton from "../../common/GeneralButton";
import { TiPlus } from "react-icons/ti";
import Icon from "../../common/Icon";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Modal from "../../common/Modal";
import { useToast } from "../../contexts/ToastContext";
import { GiMeal } from "react-icons/gi";
import axios from "../../../config/axios";
import { FaCirclePause, FaCirclePlay } from "react-icons/fa6";
import { IoRefreshCircle } from "react-icons/io5";
import { IoCalendarOutline } from "react-icons/io5";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { useGeneral } from "../../contexts/GeneralContext";
import {
  getHowManyDaysRemaining,
  getRemainingDays,
  isSubscriptionRenewable,
  is48HoursOrMore,
  isDayPassed,
  isDayPassedOrToday,
  isSnack,
} from "../../utils/commonHelper";
import { useCart } from "../../contexts/CartContext";
import FullScreenLoading from "../../common/loading/FullScreenLoading";

const SubscriptionsList = ({
  list,
  toggleAutoRenew,
  showAdd = true,
  fetchSubscriptions = null,
}) => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { fetchCart } = useCart();
  const [selectedSubId, setSelectedSubId] = useState(null);
  const [selectedRenewSubId, setSelectedRenewSubId] = useState(null);
  const [selectedRenewSub, setSelectedRenewSub] = useState(null);
  const { extraContent } = useGeneral();
  const [isLoading, setIsLoading] = useState(false);
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const handlePauseClick = (id) => setSelectedSubId(id);
  const handleRenewClick = (sub) => {
    setSelectedRenewSubId(sub?._id);
    setSelectedRenewSub(sub);
  };
  const handleCloseModal = () => setSelectedSubId(null);
  const handleRenewCloseModal = () => setSelectedRenewSubId(null);
  const handleConfirm = async () => {
    if (!selectedSubId) return;

    try {
      setIsLoading(true);

      const { data } = await axios.post("/dashboard/subscriptions/pause-plan", {
        subscriptionId: selectedSubId,
      });

      // ✅ API succeeded
      addToast("Plan paused successfully.", "success");
      setSelectedSubId(null);
      if (fetchSubscriptions) {
        fetchSubscriptions();
      }
    } catch (err) {
      console.error("Pause plan failed:", err);
      addToast(
        err?.response?.data?.message ||
          "Failed to pause plan. Please try again.",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };
  const handleReactivate = async () => {
    if (!selectedSubId) return;

    try {
      setIsLoading(true);

      const { data } = await axios.post(
        "/dashboard/subscriptions/reactivate-plan",
        {
          subscriptionId: selectedSubId,
        },
      );

      // ✅ API succeeded
      addToast("Plan reactivated successfully.", "success");
      setSelectedSubId(null);
      if (fetchSubscriptions) {
        fetchSubscriptions();
      }
    } catch (err) {
      console.error("Reactiavte plan failed:", err);
      addToast(
        err?.response?.data?.message ||
          "Failed to reactivate plan. Please try again.",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };
  const handleAutoRenew = async (id, action) => {
    try {
      setIsLoading(true);

      const isEnabling = action === "0"; // true if enabling, false if disabling

      const { data } = await axios.post("/dashboard/subscriptions/auto-renew", {
        subscriptionId: id,
        action: isEnabling ? "1" : "0",
      });

      // ✅ Show different message based on action
      addToast(
        isEnabling
          ? "Auto-renewal has been activated for your plan."
          : "Auto-renewal has been turned off for your plan.",
        "success",
      );

      setSelectedSubId(null);
      if (fetchSubscriptions) {
        fetchSubscriptions();
      }
    } catch (err) {
      console.error("Failed to update auto-renewal status.", err);

      const isEnabling = action === "0";
      addToast(
        err?.response?.data?.message ||
          (isEnabling
            ? "Failed to enable auto-renewal. Please try again."
            : "Failed to disable auto-renewal. Please try again."),
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const handleRenew = async (meal) => {
    if (!selectedRenewSubId) return;

    try {
      const mealIds =
        meal?.subscribedMeals?.map((item) => item.meal?._id) || [];

      setIsLoading(true);
      const payload = {
        customerId: loggedInUser?._id,
        previousSubscriptionId: meal?._id,
        isRenewal: "Yes",
        planId: meal?.plan?._id,
        vegOrNonVeg: meal?.vegOrNonVeg,
        mealIds: mealIds,
        childId: meal?.kid?._id,
      };

      const res = await axios.post(
        "/cart/add-renew-meal-subscription-to-cart",
        payload,
      );
      if (res.status === 200 || res.status === 201) {
        setIsLoading(false);
        addToast("Subscription added to cart!", "success");
        localStorage.setItem("mealCartId", res?.data?.cartId);
        await fetchCart();
        navigate("/checkout/meals?renew=true");
      }
    } catch (err) {
      setIsLoading(false);
      console.error("Failed to add subscription to cart:", err);
      addToast(
        "Failed to add subscription to cart. Please try again.",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.5, ease: "easeOut" }}
      className="w-full bg-[#DEE2DD]/40 rounded-[15px] p-4 lg:py-10 lg:px-12 overflow-auto"
    >
      {showAdd && (
        <div className="flex items-center justify-end">
          <GeneralButton
            text="Add new plan"
            className="text-18pt"
            onClick={() => navigate("/plans")}
            icon={<TiPlus size={20} />}
          />
        </div>
      )}

      <div className="mt-5 2.5xl:mt-12 2.5xl:mt-12 2.5xl:ms-14 2.5xl:me-36 flex flex-col gap-5 2.5xl:gap-10">
        {list?.length > 0 ? (
          list.map((sub, index) => {
            const isExpired = sub.status?.toLowerCase() === "expired";
            const isCancelled = sub.status?.toLowerCase() === "cancelled";
            const subscribedMeals = Array.isArray(sub?.subscribedMeals)
              ? sub.subscribedMeals
              : [];

            const firstMeal = subscribedMeals[0] || null;
            const planTitle = sub?.plan?.title || "N/A";
            const mealTitles = subscribedMeals
              .map((m) => (isSnack(m?.meal?.title) ? "Snacks" : m?.meal?.title))
              .filter(Boolean);

            return (
              <div
                key={index}
                className={`relative min-h-[119px] rounded-[15px] p-4 lg:ps-4 flex flex-col lg:flex-row justify-between transition-all relative ${
                  isExpired
                    ? "bg-[#f9f9f9] border border-gray opacity-90"
                    : isCancelled
                      ? "bg-[#D06F36]/5 border border-orange/20"
                      : "bg-white"
                }`}
              >
                {isLoading && <FullScreenLoading />}

                {isExpired && (
                  <div className="absolute right-4 top-2 xl:top-auto xl:bottom-4 bg-black text-white text-12pt px-3 py-1 rounded-full font-medium shadow-md w-fit">
                    Expired
                  </div>
                )}
                {isCancelled && (
                  <div className="absolute right-4 top-2 xl:top-auto xl:bottom-4 bg-red-700 text-white text-12pt px-3 py-1 rounded-full font-medium shadow-md w-fit">
                    Cancelled
                  </div>
                )}

                {/* Left: Child Info */}
                <div
                  className={`flex flex-col md:flex-row items-center cursor-pointer
                  `}
                  onClick={() => navigate(`/account/subscriptions/${sub?._id}`)}
                  title={"Click to view details"}
                >
                  <div className="w-[96px] h-[96px] bg-white border border-[#DEE2DD] rounded-full flex items-center justify-center md:me-6 lg:me-4 relative">
                    <Icon
                      icon="profile"
                      className={`w-[57px] h-[60px] ${
                        sub?.kid?.gender?.toLowerCase() === "girl"
                          ? "text-[#FC05B0]/40"
                          : "text-[#0B81FD]/40"
                      }`}
                    />
                    {sub.status.toLowerCase() === "paused" && !isExpired && (
                      <div className="absolute top-0 -right-2 lg:top-1 lg:-right-0">
                        <FaCirclePause className="text-[29px] h-auto text-orange bg-white rounded-full" />
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-22pt md:text-24pt leading-tight text-center md:text-left">
                      {sub?.kid?.firstName} {sub?.kid?.lastName} -{" "}
                      <span className="text-16pt md:text-18pt text-nowrap sm:text-wrap">
                        ({sub?.kid?.gender} - {sub?.kid?.age} years old)
                      </span>
                    </p>

                    <p className="text-16pt md:text-18pt text-quaternary mb-1 text-center md:text-left">
                      {planTitle} plan:{" "}
                      {mealTitles.join(", ") || "No meals added"}
                    </p>
                    {sub.status.toLowerCase() === "active" &&
                      !isDayPassedOrToday(sub.subscriptionStartDate) &&
                      sub.previousSubscriptionId != null && (
                        <p className="text-14pt text-green font-mono text-center md:text-left">
                          This is a renewed subscription, scheduled to start on{" "}
                          {sub.subscriptionStartDate}.
                        </p>
                      )}
                    {sub.status.toLowerCase() === "active" &&
                      (sub.previousSubscriptionId === null ||
                        isDayPassedOrToday(sub.subscriptionStartDate)) && (
                        <p className="text-14pt text-secondary font-mono text-center md:text-left">
                          Meal delivery from {sub.subscriptionStartDate} to{" "}
                          {sub.subscriptionEndDate}.
                        </p>
                      )}
                    {sub.status.toLowerCase() === "paused" && (
                      <p className="text-14pt text-orange font-mono text-center md:text-left">
                        Subscription paused
                      </p>
                    )}
                    {sub.status.toLowerCase() === "active" &&
                      sub?.totalMealsInPlan > 5 &&
                      isDayPassedOrToday(sub.subscriptionStartDate) &&
                      getHowManyDaysRemaining(sub?.subscriptionEndDate) <=
                        5 && (
                        <p className="text-14pt text-orange font-mono text-center md:text-left">
                          Expiring{" "}
                          {getHowManyDaysRemaining(sub?.subscriptionEndDate) > 0
                            ? `in ${getHowManyDaysRemaining(sub?.subscriptionEndDate)} days`
                            : "today"}{" "}
                          {sub.subscriptionRenewed === "Yes" && (
                            <span className="text-14pt text-green font-mono text-center md:text-left">
                              (Subscription renewed)
                            </span>
                          )}
                        </p>
                      )}
                    {sub.status.toLowerCase() === "active" &&
                      sub?.totalMealsInPlan <= 5 &&
                      isDayPassedOrToday(sub.subscriptionStartDate) &&
                      getHowManyDaysRemaining(sub?.subscriptionEndDate) <=
                        3 && (
                        <p className="text-14pt text-orange font-mono text-center md:text-left">
                          Expiring{" "}
                          {getHowManyDaysRemaining(sub?.subscriptionEndDate) > 0
                            ? `in ${getHowManyDaysRemaining(sub?.subscriptionEndDate)} days`
                            : "today"}{" "}
                          {sub.isRenewed === "Yes" && (
                            <span className="text-14pt text-green font-mono text-center md:text-left">
                              (Subscription renewed)
                            </span>
                          )}
                        </p>
                      )}

                    {sub?.depositReturned === "1" && (
                      <p className="text-14pt text-primary text-center md:text-left flex items-center justify-center md:justify-start">
                        Deposit Returned{" "}
                        <IoIosCheckmarkCircle size={18} className="ms-1" />
                      </p>
                    )}
                  </div>
                </div>

                {/* Right: Actions */}
                {!isCancelled ? (
                  <div className="mt-4 md:mt-0 flex flex-col md:flex-row-reverse justify-between items-center  lg:items-end w-full lg:flex lg:flex-col lg:justify-start lg:w-auto">
                    {/* Action icons */}

                    <div className="flex justify-end gap-7">
                      {!isDayPassedOrToday(sub.subscriptionStartDate) &&
                        sub.previousSubscriptionId === null && (
                          <IoCalendarOutline
                            title="Click to change start date"
                            onClick={() =>
                              navigate(
                                `/account/subscriptions/customize/date/${sub._id}`,
                              )
                            }
                            className="text-[28px] text-green hover:text-secondary"
                          />
                        )}
                      {sub.status.toLowerCase() === "active" && (
                        <div
                          className=" cursor-pointer"
                          title="Click to customize meals"
                        >
                          <Icon
                            icon="customize"
                            className="w-[30px] text-green hover:text-secondary"
                            onClick={() =>
                              navigate(
                                `/account/subscriptions/customize/${sub._id}`,
                              )
                            }
                          />
                        </div>
                      )}

                      {/* <div
                        className=" cursor-pointer"
                        title="Click to upgrade plan"
                      >
                        <Icon
                          icon="edit"
                          onClick={() =>
                            navigate(
                              `/account/subscriptions/upgrade/${sub._id}`
                            )
                          }
                          className="w-[25px] lg:w-[30px] text-green hover:text-secondary"
                        />
                      </div> */}
                      {sub.status.toLowerCase() === "paused" &&
                        isDayPassedOrToday(sub.subscriptionStartDate) && (
                          <div
                            className=" cursor-pointer"
                            title={
                              selectedSubId === sub._id
                                ? ""
                                : "Click to reactivate plan"
                            }
                          >
                            <FaCirclePlay
                              onClick={() => handlePauseClick(sub._id)}
                              className="text-[30px] text-green hover:text-secondary"
                            />
                            {selectedSubId === sub._id && (
                              <Modal
                                isOpen={true}
                                onClose={handleCloseModal}
                                title="Reactivate Subscription"
                                onConfirm={handleReactivate}
                              >
                                <div className="font-mono">
                                  {/* <p>
                                  You’re requesting to reactivate your{" "}
                                  <span className="font-semibold">
                                    {planTitle} plan ({mealTitles.join(", ")})
                                  </span>{" "}
                                  for {sub.kid.firstName} {sub.kid.lastName} on{" "}
                                  {today}.
                                </p> */}
                                  <p className="mt-2">
                                    {
                                      extraContent?.mealReactivateMessages
                                        ?.line_1
                                    }
                                  </p>
                                  <p className="mt-2 ">
                                    {
                                      extraContent?.mealReactivateMessages
                                        ?.line_2
                                    }
                                  </p>
                                </div>
                              </Modal>
                            )}
                          </div>
                        )}
                      {sub.status.toLowerCase() != "paused" &&
                        is48HoursOrMore(sub?.subscriptionEndDate) &&
                        isDayPassedOrToday(sub?.subscriptionStartDate) &&
                        (sub?.pausedOneTime != "1" ||
                          sub?.pausedOneTime != 1) && (
                          <div
                            className=" cursor-pointer"
                            title="Click to pause plan"
                          >
                            <FaCirclePause
                              onClick={() => handlePauseClick(sub._id)}
                              className="text-[30px] text-green hover:text-secondary"
                            />
                            {selectedSubId === sub._id && (
                              <Modal
                                isOpen={true}
                                onClose={handleCloseModal}
                                title="Pause Subscription"
                                onConfirm={handleConfirm}
                              >
                                <div className="font-mono">
                                  {/* <p>
                                  You’re requesting to pause your{" "}
                                  <span className="font-semibold">
                                    {planTitle} plan ({mealTitles.join(", ")})
                                  </span>{" "}
                                  for {sub.kid.firstName} {sub.kid.lastName} on{" "}
                                  {today}.
                                </p> */}
                                  <p className="mt-2">
                                    {extraContent?.mealPauseMessages?.line_1}
                                  </p>
                                  <p className="mt-2 ">
                                    {extraContent?.mealPauseMessages?.line_2}
                                  </p>
                                </div>
                              </Modal>
                            )}
                          </div>
                        )}
                      {sub.status.toLowerCase() === "active" &&
                        is48HoursOrMore(sub?.subscriptionEndDate) &&
                        isDayPassedOrToday(sub.subscriptionStartDate) && (
                          <div
                            className="cursor-pointer"
                            title="Click to cancel plan"
                          >
                            <Icon
                              icon="mealCancel"
                              onClick={() =>
                                navigate(
                                  `/account/subscriptions/cancel/${sub._id}`,
                                )
                              }
                              className="w-[30px] text-green hover:text-secondary"
                            />
                          </div>
                        )}
                      {sub?.isRenewed != "Yes" &&
                        sub?.status.toLowerCase() != "paused" &&
                        (sub?.status.toLowerCase() === "expired" ||
                          isSubscriptionRenewable(
                            sub?.subscriptionStartDate,
                          )) && (
                          <div className=" cursor-pointer">
                            <IoRefreshCircle
                              title="Click to renew plan"
                              onClick={() => handleRenewClick(sub)}
                              className={
                                "text-[35px]  h-auto text-green hover:text-secondary"
                              }
                            />
                          </div>
                        )}
                    </div>
                    {/* {sub.status.toLowerCase() === "active" && (
                      <div
                        className="flex items-end justify-end mt-4 cursor-pointer select-none"
                        onClick={() => handleAutoRenew(sub._id, sub.autoRenew)}
                      >
                        <div className="me-2">
                          <Icon
                            icon={
                              sub.autoRenew === "1" ? "checked" : "checkbox"
                            }
                            className="w-[18px] h-auto"
                          />
                        </div>
                        <p className="text-green hover:text-secondary text-14pt font-mono">
                          Auto renew subscription
                        </p>
                      </div>
                    )} */}
                  </div>
                ) : (
                  isCancelled && (
                    <div className="text-gray-700 text-14pt font-mono text-center md:text-right w-full lg:w-auto">
                      This subscription has been cancelled.
                    </div>
                  )
                )}
              </div>
            );
          })
        ) : (
          <div className="w-full flex justify-center items-center py-8 px-4">
            <div className="text-quaternary px-6 py-4 rounded-md text-center max-w-xl w-full flex flex-col items-center gap-2">
              <GiMeal size={80} className="text-quaternary" />
              <p className="text-lg">No Active Subscriptions</p>
              <p className="text-sm text-quaternary font-mono">
                Subscribe now to explore more details
              </p>
            </div>
          </div>
        )}
      </div>
      {selectedRenewSubId && (
        <Modal
          isOpen={true}
          onClose={handleRenewCloseModal}
          title="Renew Subscription"
          onConfirm={() => handleRenew(selectedRenewSub)}
        >
          <div className="font-mono">
            <p className="mt-2">
              Are you sure you want to renew this subscription?
            </p>
          </div>
        </Modal>
      )}
    </motion.div>
  );
};

export default SubscriptionsList;
